import React from 'react';
import PropTypes from 'prop-types';
import { A11yText, Divider, selectSystemProps, Typography, useThemeTokens, useViewport, getTokensPropType, a11yProps } from '@telus-uds/components-base';
import styled from 'styled-components';
import FootnoteLink from '../Footnote/FootnoteLink';
import getTypographyTokens from './tokens';
import { htmlAttrs, warn } from '../utils';
import defaultDictionary from './dictionary';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const PRICE_BASELINE_VARIANT = 'baseline';
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs, a11yProps]);
const PriceLockupContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__PriceLockupContainer",
  componentId: "components-web__sc-1x6duay-0"
})(["align-items:", ";display:flex;flex-direction:column;width:fit-content;"], _ref => {
  let {
    alignItemsText
  } = _ref;
  return alignItemsText;
});
const PriceContainer = /*#__PURE__*/styled.div.attrs({
  'aria-hidden': 'true'
}).withConfig({
  displayName: "PriceLockup__PriceContainer",
  componentId: "components-web__sc-1x6duay-1"
})(["display:flex;margin-bottom:", ";"], _ref2 => {
  let {
    priceMarginBottom
  } = _ref2;
  return priceMarginBottom;
});
const FootnoteContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__FootnoteContainer",
  componentId: "components-web__sc-1x6duay-2"
})(["display:flex;margin-top:", ";gap:", ";"], _ref3 => {
  let {
    footnoteMarginTop
  } = _ref3;
  return footnoteMarginTop;
}, _ref4 => {
  let {
    footnoteGap
  } = _ref4;
  return footnoteGap;
});
const BottomTextContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__BottomTextContainer",
  componentId: "components-web__sc-1x6duay-3"
})(["margin-top:", ";"], _ref5 => {
  let {
    bottomTextMarginTop
  } = _ref5;
  return bottomTextMarginTop;
});
const BottomLinksContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__BottomLinksContainer",
  componentId: "components-web__sc-1x6duay-4"
})(["align-self:center;margin-left:", ";"], _ref6 => {
  let {
    bottomLinksMarginLeft
  } = _ref6;
  return bottomLinksMarginLeft;
});
const TopTextContainer = /*#__PURE__*/styled.div.attrs({
  'aria-hidden': 'true'
}).withConfig({
  displayName: "PriceLockup__TopTextContainer",
  componentId: "components-web__sc-1x6duay-5"
})(["margin-bottom:", ";"], _ref7 => {
  let {
    topTextMarginBottom
  } = _ref7;
  return topTextMarginBottom;
});
const PriceTextContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__PriceTextContainer",
  componentId: "components-web__sc-1x6duay-6"
})(["display:flex;flex-direction:", ";"], _ref8 => {
  let {
    ratePosition
  } = _ref8;
  return ratePosition === 'bottom' ? 'column' : 'row';
});
const RateContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__RateContainer",
  componentId: "components-web__sc-1x6duay-7"
})(["display:flex;"]);
const RateTextContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__RateTextContainer",
  componentId: "components-web__sc-1x6duay-8"
})(["align-self:", ";"], _ref9 => {
  let {
    ratePosition
  } = _ref9;
  return ratePosition === 'bottom' ? 'flex-start' : 'flex-end';
});
const StrikeThroughContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__StrikeThroughContainer",
  componentId: "components-web__sc-1x6duay-9"
})(["display:flex;position:relative;align-items:center;::before{content:'';width:100%;top:", ";height:", ";background:", ";position:absolute;}"], _ref10 => {
  let {
    strikeThroughPosition
  } = _ref10;
  return `${strikeThroughPosition}px`;
}, _ref11 => {
  let {
    strikeThroughHeight
  } = _ref11;
  return `${strikeThroughHeight}px`;
}, _ref12 => {
  let {
    strikeThroughColor
  } = _ref12;
  return strikeThroughColor;
});
const TypographyContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__TypographyContainer",
  componentId: "components-web__sc-1x6duay-10"
})(["display:flex;padding-top:", ";"], _ref13 => {
  let {
    paddingTop
  } = _ref13;
  return `${paddingTop || 0}px`;
});
const InlineTextWithFootnote = /*#__PURE__*/styled.div.withConfig({
  displayName: "PriceLockup__InlineTextWithFootnote",
  componentId: "components-web__sc-1x6duay-11"
})(["display:inline;position:relative;> *:first-child{display:inline;}> *:last-child{display:inline-block;position:relative;top:-0.5em;font-size:0.75em;margin-left:2px;line-height:0;vertical-align:bottom;}"]);
const selectFootnoteLinkStyles = _ref14 => {
  let {
    footnoteLinkColor,
    footnoteLinkFontName,
    footnoteLinkFontWeight,
    footnoteLinkLineHeight
  } = _ref14;
  return {
    color: footnoteLinkColor,
    fontName: footnoteLinkFontName,
    fontWeight: footnoteLinkFontWeight,
    lineHeight: footnoteLinkLineHeight
  };
};
const selectStrikeThroughTokens = _ref15 => {
  let {
    strikeThroughPosition,
    strikeThroughHeight,
    strikeThroughColor
  } = _ref15;
  return {
    strikeThroughHeight,
    strikeThroughPosition,
    strikeThroughColor
  };
};
const PriceLockup = /*#__PURE__*/React.forwardRef((_ref16, ref) => {
  let {
    size = 'medium',
    signDirection = 'left',
    footnoteLinks = [],
    topText,
    price,
    currencySymbol = '$',
    rateText,
    ratePosition = 'right',
    bottomText,
    onClickFootnote,
    strikeThrough,
    a11yText,
    linkPosition = 'default',
    tokens: priceLockupTokens,
    variant = {},
    copy = 'en',
    dictionary = defaultDictionary,
    ...rest
  } = _ref16;
  const viewport = useViewport();
  const {
    footnoteMarginTop,
    footnoteGap,
    bottomTextMarginTop,
    priceMarginBottom,
    bottomLinksMarginLeft,
    topTextMarginBottom,
    fontColor,
    dividerColor,
    footnoteLinkFontSize,
    alignItemsText,
    ...themeTokens
  } = useThemeTokens('PriceLockup', priceLockupTokens, {
    ...variant,
    size
  }, {
    viewport,
    strikeThrough
  });
  const isPriceBaseline = variant?.price === PRICE_BASELINE_VARIANT;
  const typographyTokens = getTypographyTokens(themeTokens);
  const priceString = String(price);
  const lastDotPosition = priceString.lastIndexOf('.');
  const lastCommaPosition = priceString.lastIndexOf(',');
  const [separator, separatorPosition] = lastDotPosition > lastCommaPosition ? ['.', lastDotPosition] : [',', lastCommaPosition];

  // If the separator is at the fourth character from the end of the string or more, it's most probably
  // a part of the amount, and the cents are not included in the price string
  const hasCents = separatorPosition !== -1 && separatorPosition >= priceString.length - 3;
  const amount = hasCents ? priceString.substring(0, separatorPosition) : priceString;
  const cents = hasCents ? priceString.substring(separatorPosition + 1) : null;
  const renderTypography = (value, tokens, position) => {
    const {
      paddingTop,
      ...restOfTokens
    } = tokens;
    const customProps = position === 'bottom' ? {
      variant: {
        size: 'micro'
      },
      tokens: {
        color: fontColor
      }
    } : {
      tokens: {
        ...restOfTokens,
        color: fontColor
      }
    };
    return /*#__PURE__*/_jsx(TypographyContainer, {
      paddingTop: paddingTop,
      children: /*#__PURE__*/_jsx(Typography, {
        ...customProps,
        children: value
      })
    });
  };
  const renderCurrencySymbol = () => renderTypography(`${currencySymbol}`, typographyTokens.dollarSign);
  const renderFootnoteLinks = () => footnoteLinks && footnoteLinks.length > 0 ? /*#__PURE__*/_jsx(FootnoteLink, {
    fontSize: footnoteLinkFontSize,
    tokens: selectFootnoteLinkStyles(themeTokens),
    number: footnoteLinks,
    onClick: onClickFootnote
  }) : null;
  const renderAmount = () => {
    const amountComponent = renderTypography(amount, typographyTokens.amount);
    if (strikeThrough) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(A11yText, {
          text: a11yText
        }), /*#__PURE__*/_jsx(StrikeThroughContainer, {
          ...selectStrikeThroughTokens(themeTokens),
          children: amountComponent
        })]
      });
    }
    return amountComponent;
  };
  const renderPrice = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(PriceContainer, {
      priceMarginBottom: `${priceMarginBottom}px`,
      children: [/*#__PURE__*/_jsxs(PriceTextContainer, {
        ratePosition: ratePosition,
        children: [/*#__PURE__*/_jsxs(RateContainer, {
          children: [signDirection === 'left' && renderCurrencySymbol(), renderAmount(), cents && renderTypography(`${separator}${cents}`, typographyTokens.cents), signDirection === 'right' && /*#__PURE__*/_jsxs(_Fragment, {
            children: ["\xA0", renderCurrencySymbol()]
          })]
        }), rateText && /*#__PURE__*/_jsx(RateTextContainer, {
          ratePosition: ratePosition,
          children: renderTypography(rateText, typographyTokens.rate, ratePosition)
        })]
      }), !bottomText && footnoteLinks.length <= 3 && /*#__PURE__*/_jsx(BottomLinksContainer, {
        bottomLinksMarginLeft: `${bottomLinksMarginLeft}px`,
        children: renderFootnoteLinks()
      })]
    }), !bottomText && footnoteLinks.length > 3 && renderFootnoteLinks()]
  });
  const renderFootnoteContent = () => {
    const containerProps = {
      footnoteMarginTop: `${footnoteMarginTop}px`,
      footnoteGap: `${footnoteGap}px`
    };
    const shouldUseInline = linkPosition === 'inline';
    const bottomTextContent = shouldUseInline ? /*#__PURE__*/_jsxs(InlineTextWithFootnote, {
      children: [/*#__PURE__*/_jsx(Typography, {
        tokens: typographyTokens.bottomText,
        as: "span",
        children: bottomText
      }), renderFootnoteLinks()]
    }) : renderTypography(bottomText, typographyTokens.bottomText);
    const showFootnotesInContainer = !shouldUseInline && footnoteLinks.length <= 3;
    const showFootnotesOutside = !shouldUseInline && footnoteLinks.length > 3;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(FootnoteContainer, {
        ...containerProps,
        children: [/*#__PURE__*/_jsx(BottomTextContainer, {
          bottomTextMarginTop: `${bottomTextMarginTop}px`,
          children: bottomTextContent
        }), showFootnotesInContainer && renderFootnoteLinks()]
      }), showFootnotesOutside && renderFootnoteLinks()]
    });
  };
  if (strikeThrough && !a11yText) {
    warn('PriceLockup', 'a11yText must be provided with strikethrough pricing');
  }

  /**
   * Converts rateText to string if rateText is an JSX.
   */
  function getRateText(rateTextValue) {
    if (!rateTextValue) return '';
    switch (typeof rateTextValue) {
      case 'string':
        return rateTextValue.replace('/', '');
      case 'object':
        if (/*#__PURE__*/React.isValidElement(rateTextValue)) {
          return React.Children.toArray(rateTextValue.props.children).map(getRateText).join('');
        }
        // If it's an object but not a React element, return empty string
        break;
      default:
        break;
    }
    return '';
  }
  const getAriaContent = () => {
    const {
      price: dictionaryPrice,
      priceWithCents,
      priceLeft,
      priceWithCentsLeft,
      rate,
      rateOnly
    } = dictionary[copy];
    const priceTemplate = isPriceBaseline ? priceLeft : dictionaryPrice;
    const priceWithCentsTemplate = isPriceBaseline ? priceWithCentsLeft : priceWithCents;
    let ariaLabel = hasCents ? priceWithCentsTemplate?.replace('%{amount}', amount).replace('%{separator}', separator).replace('%{cents}', cents).replace('%{currency}', currencySymbol) : priceTemplate?.replace('%{amount}', amount).replace('%{currency}', currencySymbol);
    if (!ariaLabel?.includes(currencySymbol)) {
      ariaLabel = `${ariaLabel} ${currencySymbol}`;
    }
    if (rateText) {
      if (isPriceBaseline) {
        ariaLabel += ` ${rateOnly.replace('%{rateText}', getRateText(rateText))}`;
      } else {
        ariaLabel += ` ${rate.replace('%{rateText}', getRateText(rateText))}`;
      }
    }
    return ariaLabel;
  };
  const ariaLabel = selectProps(rest)['aria-label'] ?? getAriaContent();
  return /*#__PURE__*/_jsxs(PriceLockupContainer, {
    ...selectProps(rest),
    alignItemsText: alignItemsText,
    ref: ref,
    role: "group",
    "aria-label": ariaLabel,
    children: [topText && /*#__PURE__*/_jsx(TopTextContainer, {
      topTextMarginBottom: `${topTextMarginBottom}px`,
      children: renderTypography(topText, typographyTokens.topText)
    }), renderPrice(), bottomText && /*#__PURE__*/_jsx(Divider, {
      testID: "price-lockup-divider",
      role: "separator",
      tokens: {
        color: dividerColor ?? 'rgba(0, 0, 0, 0)'
      }
    }), bottomText && renderFootnoteContent()]
  });
});
PriceLockup.displayName = 'PriceLockup';

// If a language dictionary entry is provided, it must contain every key
const dictionaryContentShape = PropTypes.shape({
  price: PropTypes.string.isRequired,
  priceWithCents: PropTypes.string.isRequired,
  priceLeft: PropTypes.string.isRequired,
  priceWithCentsLeft: PropTypes.string.isRequired,
  rate: PropTypes.string.isRequired,
  rateOnly: PropTypes.string.isRequired
});
PriceLockup.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Size of the component
   *
   * Micro for mini cart pricing, small for pricing in product catalogue pages, medium for pricing in product comparison cards and large for pricing in banners and promo cards
   */
  size: PropTypes.oneOf(['micro', 'small', 'medium', 'large']),
  /**
   * If currency symbol other than `$` to be used
   */
  currencySymbol: PropTypes.string,
  /**
   * Shows additional info above the price
   */
  topText: PropTypes.string,
  /**
   * Monetary value (including decimals separated by ".")
   */
  price: PropTypes.string.isRequired,
  /**
   * Shows month/year unit
   */
  rateText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Shows additional info below the price with a `Divider`
   */
  bottomText: PropTypes.string,
  /**
   * Displays which side the currency should apperar (left, right)
   */
  signDirection: PropTypes.oneOf(['right', 'left']),
  /**
   * Displays where the rate should apperar (bottom, right)
   */
  ratePosition: PropTypes.oneOf(['right', 'bottom']),
  /**
   * Shows additional link for context
   */
  footnoteLinks: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  /**
   * Function to be called when a footnote link is clicked
   */
  onClickFootnote: PropTypes.func,
  /**
   * To show price savings comparison
   */
  strikeThrough: PropTypes.bool,
  /**
   * To provide a11y text for `PriceLockup` component
   *
   * **Note:** a11yText will override strikethrough price, so it must include price (ie. "was 50 dollars per month")
   */
  a11yText: PropTypes.string,
  /**
   * Select English or French copy for the accessible label.
   */
  copy: PropTypes.oneOf(['en', 'fr']),
  /**
   * Controls footnote link positioning when bottomText is present
   *
   * - 'default': Footnote appears next to the text container (current behavior)
   * - 'inline': Footnote appears inline at the end of the text
   */
  linkPosition: PropTypes.oneOf(['default', 'inline']),
  /* Custom dictionary containing the labels
   */
  dictionary: PropTypes.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  }),
  /**
   * `PriceLockup` tokens
   */
  tokens: getTokensPropType('PriceLockup')
};
export default PriceLockup;